/* eslint-disable camelcase */
import React, {
    useState, useEffect, FC
} from "react";
import { GroupListItem } from "@themuse/design-system/lib/components/GroupList/GroupList";
import {
    useSearchDispatch,
    useSearchState
} from "hooks/SearchContext";
import {
    FiltersActionTypes,
    LocationActionTypes
} from "interfaces/context/searchContextInterfaces";
import { getSelectedFiltersCount } from "utils/searchFacets";
import {
    experienceLevelItems,
    companySizeItems,
    jobCategoryItems,
    searchRadiusItems,
    postedDateRangeItems,
    diversityInclusionItems,
    companyIndustryItems,
    flattenPerksAndBenefits
} from "../../utils/searchFacetItems";
import { useSearchFacetsState } from "../../hooks/SearchFacetsContext";
import { FilterTrigger } from "./FilterTrigger";

import { IconFilter } from "../icons/IconFilter";
import { FilterPanelCollection } from "./FilterPanelCollection";

import styles from "./FilterNav.module.scss";
import filterTriggerStyles from "./FilterTrigger.module.scss";

export interface Filter {
    id: string;
    label: string;
    options: GroupListItem[];
    solo: boolean;
    hasIcon: boolean;
}

export interface OpenState {
    postedDateRange: boolean;
    experienceLevel: boolean;
    remote: boolean;
    radius: boolean;
    category: boolean;
    companySize: boolean;
    perks: boolean;
    industry: boolean;
    companyLeadership: boolean;
}

const hasSelections = (filter) => filter.count > 0 || (filter.count === 0 && filter.active);

// all buttons should be closed by default
const defaultButtonsStates = {
    postedDateRange: false,
    experienceLevel: false,
    remote: false,
    radius: false,
    category: false,
    companySize: false,
    perks: false,
    industry: false,
    companyLeadership: false
};

// default location state
const nullLocation = {
    userInputValue: "",
    userSelectedLocation: {
        label: "",
        latitude: null,
        longitude: null,
        value: "",
        remote_work_location_type: null
    }
};

// the remote location state
const remoteLocation = {
    userInputValue: "Remote / Flexible",
    userSelectedLocation: {
        label: "Remote / Flexible",
        value: "Remote / Flexible",
        latitude: null,
        longitude: null,
        remote_work_location_type: "virtual",
        id: 0
    }
};

export interface FilterNavProps {
    onClear?: () => void;
}

/**
 * `FilterNav` is a React functional component that renders a navigation bar
 * for filtering job search results. It manages the state of filter options
 * and handles user interactions such as opening and closing filter menus,
 * resetting filters, and updating search criteria based on user selections.
 */
export const FilterNav: FC<FilterNavProps> = ({ onClear }) => {
    const {
        company,
        location,
        keyword,
        filters
    } = useSearchState();
    const { userSelectedCompany } = company;
    const { userSelectedLocation } = location;
    const { companyFacets: { perks_and_benefits } } = useSearchFacetsState();
    const searchDispatch = useSearchDispatch();
    const [openState, setOpenState] = useState<OpenState>({
        ...defaultButtonsStates
    });
    const [openMega, setOpenMega] = useState(false);

    /**
     * Handles click events outside of specified filter panel elements.
     */
    const handleClickOutside = (event: Event): void => {
        const target = event.target as HTMLElement;
        let currentElement: HTMLElement | null = target;

        const prefixes: string[] = [
            "FilterTrigger_FilterPanelBase",
            "FilterPanelCollection_container",
            "FilterPanel_container"
        ];

        const checkPrefix = (prefix: string) => currentElement.className.toString().startsWith(prefix);

        // check this element and its parents
        while (currentElement) {
            if (currentElement.className && prefixes.some(checkPrefix)) {
                return; // Stop execution if a matching element is found
            }
            currentElement = currentElement.parentElement;
        }

        // If no matching parent element is found, execute the closing logic
        setOpenMega(false);
        setOpenState({
            ...defaultButtonsStates
        });
    };

    // useEffect to add event listener for click outside of filter panels.
    useEffect(() => {
        // Add event listener when component mounts
        document.addEventListener("click", handleClickOutside);

        // Remove event listener when component unmounts
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    // Determines if a filter option should be disabled based on the current user selections.
    const isDisabled = (id: string) => {
        const hasLocation = !!userSelectedLocation.value;
        const isLocationProp = ["radius"].includes(id);
        const hasCompany = !!userSelectedCompany?.value;
        const isCompanyProp = ["companySize", "industry", "perks", "companyLeadership"].includes(id);
        return !!((isCompanyProp && hasCompany)
            || (isLocationProp && !hasLocation)
            || (isLocationProp && hasLocation && userSelectedLocation.remote_work_location_type === "virtual"));
    };

    const perks = flattenPerksAndBenefits(perks_and_benefits);

    // list of buttons
    // * hasIcon: show the open close arrow icon
    // * solo: should show in the top nav bar independently of the mega dropdown.
    const buttonOptions: Filter[] = [
        {
            id: "postedDateRange", label: "Date Posted", hasIcon: true, solo: true, options: postedDateRangeItems
        },
        {
            id: "experienceLevel", label: "Experience Level", hasIcon: true, solo: true, options: experienceLevelItems
        },
        {
            id: "remote", label: "Remote/Flexible Only", hasIcon: false, solo: true, options: []
        },
        {
            id: "radius", label: "Search Radius", hasIcon: true, solo: true, options: searchRadiusItems
        },
        {
            id: "category", label: "Job Category", hasIcon: true, solo: true, options: jobCategoryItems
        },
        {
            id: "perks", label: "Perks and Benefits", hasIcon: true, solo: true, options: perks
        },
        {
            id: "companySize", label: "Company Size", hasIcon: true, solo: true, options: companySizeItems
        },
        {
            id: "industry", label: "Company Industry", hasIcon: true, solo: false, options: companyIndustryItems
        },
        {
            id: "companyLeadership",
            label: "Diversity & Inclusion",
            hasIcon: true,
            solo: false,
            options: diversityInclusionItems
        }
    ];

    // Enabled with selections come first
    const selectedFilters = buttonOptions.filter((btn) => hasSelections(btn) && !!btn.solo && !isDisabled(btn.id));
    // Enabled without selections come after
    const unSelectedFilters = buttonOptions.filter((x) => !hasSelections(x) && !!x.solo && !isDisabled(x.id));
    // Disabled always come last
    const disabledFilters = buttonOptions.filter((x) => !!x.solo && isDisabled(x.id));
    // Everything else.
    const extraFilters = buttonOptions.filter((x) => !x.solo);
    // Total count of all filters and their options currently active
    const filterCount = getSelectedFiltersCount({
        filters, location, company, keyword
    });

    const remoteSubmit = (id: string): void => {
        if (id === "remote" && !userSelectedLocation.remote_work_location_type) {
            searchDispatch({
                type: LocationActionTypes.updateLocation,
                payload: remoteLocation
            });

            if (filters?.radius) {
                searchDispatch({
                    type: FiltersActionTypes.updateFilter,
                    payload: {
                        radius: ""
                    }
                });
            }
        }
        if (id === "remote" && userSelectedLocation.remote_work_location_type) {
            searchDispatch({
                type: LocationActionTypes.updateLocation,
                payload: nullLocation
            });
        }
    };

    /**
     * Toggles the open state of a specific filter panel identified by the given key.
     */
    const toggleMenu = (key: string) => {
        setOpenState((prev) => ({
            ...defaultButtonsStates,
            [key]: !prev[key],
        }));
    };

    /**
     * Toggles the state of the mega menu.
     *
     * This function resets the open state of buttons to their default
     * and toggles the open state of the mega menu.
     */
    const toggleMegaMenu = () => {
        setOpenState(() => ({
            ...defaultButtonsStates,
        }));
        setOpenMega((prev) => (!prev));
    };

    /**
     * Toggles the visibility of a specific menu panel based on the provided key.
     *
     * If the key is "remote", it triggers a remote submission instead of toggling.
     * Updates the state of the panel and button visibility.
     */
    const toggleSoloMenu = (key: string, closeMegaMenu?: boolean) => {
        // the remote button does not have a menu so we instead submit here
        if (key === "remote") {
            remoteSubmit(key);
        }
        // update the panel and button states
        toggleMenu(key);

        if (closeMegaMenu) {
            setOpenMega(false);
        }
    };

    /**
     * Toggles the open state of a specific filter button and closes the mega menu.
     */
    const onCancel = (key: string) => {
        toggleMenu(key);
        setOpenMega(false);
    };

    return (
        <nav className={styles.allFilters} aria-label="Job filters" data-mega-open={openMega}>
            {/* Individual Filter buttons */}
            <div className={styles.buttonWrapper}>
                {selectedFilters.map((filter) => (
                    <FilterTrigger
                        options={filter.options}
                        key={filter.label}
                        id={filter.id}
                        disabled={isDisabled(filter.id)}
                        onClick={() => toggleSoloMenu(filter.id, true)}
                        onCancel={() => onCancel(filter.id)}
                        onBackBtn={() => toggleMenu(filter.id)}
                        label={filter.label}
                        hasIcon={filter.hasIcon}
                        isOpen={openState[filter.id]}
                        isMegaMenu={false}
                    />
                ))}

                {unSelectedFilters.map((filter) => (
                    <FilterTrigger
                        options={filter.options}
                        key={filter.label}
                        id={filter.id}
                        disabled={isDisabled(filter.id)}
                        onClick={() => toggleSoloMenu(filter.id, true)}
                        onCancel={() => onCancel(filter.id)}
                        onBackBtn={() => toggleMenu(filter.id)}
                        label={filter.label}
                        hasIcon={filter.hasIcon}
                        isOpen={openState[filter.id]}
                        isMegaMenu={false}
                    />
                ))}

                {disabledFilters.map((filter) => (
                    <FilterTrigger
                        options={filter.options}
                        key={filter.label}
                        id={filter.id}
                        disabled={isDisabled(filter.id)}
                        onClick={() => toggleSoloMenu(filter.id, true)}
                        onCancel={() => onCancel(filter.id)}
                        onBackBtn={() => toggleMenu(filter.id)}
                        label={filter.label}
                        hasIcon={filter.hasIcon}
                        isOpen={openState[filter.id]}
                        isMegaMenu={false}
                    />
                ))}

                {extraFilters.map((filter) => (
                    <FilterTrigger
                        options={filter.options}
                        key={filter.label}
                        id={filter.id}
                        disabled={isDisabled(filter.id)}
                        onClick={() => toggleSoloMenu(filter.id, true)}
                        onCancel={() => onCancel(filter.id)}
                        onBackBtn={() => toggleMenu(filter.id)}
                        label={filter.label}
                        hasIcon={filter.hasIcon}
                        isOpen={openState[filter.id]}
                        isMegaMenu={false}
                    />
                ))}
            </div>
            <div className={styles.resetWrapper}>
                {/* Reset Button */}
                <button
                    type="button"
                    className={styles.reset}
                    onClick={onClear}
                    aria-label="Reset all filters"
                >
                    Reset All
                </button>
                {/* Mega Menu Button */}
                <button
                    className={
                        `${filterTriggerStyles.FilterPanelBase} ${filterCount ? filterTriggerStyles.active : ""}`
                    }
                    type="button"
                    tabIndex={0}
                    aria-label="All Filters"
                    onClick={toggleMegaMenu}
                >
                    <div className={`${styles.icons} ${styles.filterIcon}`}>
                        <IconFilter className={`${styles.filterIcon} ${openMega ? styles.open : ""}`} />
                    </div>

                    <div className={styles.inputWrapper}>All Filters</div>

                    {filterCount
                        ? (
                            <div
                                className={styles.counter}
                                aria-label={`${filterCount} selected`}
                            >
                                {filterCount}
                            </div>
                        ) : <div>{" "}</div>}
                </button>
                {/* Mega Menu Panel */}
                <FilterPanelCollection {...{
                    isOpen: openMega,
                    buttonOptions,
                    onCancel: () => setOpenMega(false),
                    onApply: () => setOpenMega(false),
                    onBackBtn: toggleMenu,
                    openState,
                    toggleSoloMenu,
                    toggleMegaMenu,
                    reset: onClear
                }}
                />
            </div>
        </nav>
    );
};
